import { Slot } from '@radix-ui/react-slot';
import { type ButtonHTMLAttributes, forwardRef } from 'react';

import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'n1' | 'n2' | 'n3';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-800 text-white hover:bg-brand-900 active:bg-brand-900 disabled:bg-brand-500',
  secondary:
    'bg-brand-50 text-brand-900 hover:bg-brand-100 active:bg-brand-100 disabled:bg-bg-muted disabled:text-neutral-400',
  ghost:
    'bg-transparent text-brand-800 hover:bg-brand-50 active:bg-brand-100 disabled:text-neutral-400',
  danger:
    'bg-danger-500 text-white hover:bg-danger-700 active:bg-danger-700 disabled:bg-danger-500/40',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  n1: 'min-h-[44px] min-w-[44px] px-4 text-base',
  n2: 'min-h-[48px] min-w-[48px] px-5 text-base',
  n3: 'min-h-[60px] min-w-[60px] px-6 text-lg',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'n2', asChild = false, className, type, ...rest }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : (type ?? 'button')}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
          'transition-colors duration-150',
          'disabled:cursor-not-allowed',
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          className,
        )}
        data-variant={variant}
        data-size={size}
        {...rest}
      />
    );
  },
);
Button.displayName = 'Button';
